import type { Workspace } from '@carboncopyco/types';
import { Button, TextBase, TextCaption, notify } from '@carboncopyco/ui';
import { useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { type Col, FixedTable } from '../../FixedTable';
import { WorkspaceAvatar } from '../WorkspaceAvatar';
import { useWorkspacesPage } from './WorkspacesPageContext';
import { WorkspacesPageEmpty } from './WorkspacesPageEmpty';
import { WorkspacesPageTableRowWrapper } from './WorkspacesPageTableRowWrapper';
import { WorkspacesPageTableSkeleton } from './WorkspacesPageTableSkeleton';
import { WorkspacesPageTagsCell } from './WorkspacesPageTagsCell';

const typeLabels = {
  default: 'Public (default)',
  private: 'Private',
  public: 'Public',
  playgorund: 'Draft',
};

export const WorkspacesPageTable = () => {
  const {
    organizationId,
    workspaces,
    handleFetchNextBatch,
    isLoading,
    isLoadingBatch,
    replaceItem,
    joinWorkspace,
  } = useWorkspacesPage();

  const { tags } = useWorkspacesPage();
  const cols: Col<Workspace>[] = useMemo(
    () => [
      {
        alignment: 'left',
        title: 'Name',
        width: 420,
        maxContentWidth: 410,
        render: (workspace) => {
          return (
            <div className="flex gap-2 items-center">
              <WorkspaceAvatar
                workspace={workspace}
                size="small"
              />
              <div className="flex flex-col w-[440px]">
                <TextBase
                  ellipsis
                  color="text-ds-natural-600"
                  label={`${workspace.workspaceName} ${workspace.isDeleted ? '(Archvied)' : ''}`}
                />
                <TextCaption label={typeLabels[workspace.workspaceType]} />
              </div>
            </div>
          );
        },
      },
      {
        alignment: 'center',
        title: 'Users',
        width: 120,
        maxContentWidth: 80,
        render: (workspace) => (
          <div className="flex justify-center items-center">
            <TextBase
              color="text-ds-natural-600"
              label={workspace?.totalMembers}
            />
          </div>
        ),
      },
      {
        alignment: 'center',
        title: 'Access',
        width: 120,
        maxContentWidth: 80,
        render: (workspace) => (
          <div className="flex justify-center items-center">
            <TextBase
              color="text-ds-natural-600"
              label={
                workspace?.isMember
                  ? 'Joined'
                  : !workspace.isDeleted && (
                      <Button
                        xSmall
                        secondary
                        label="Join"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          joinWorkspace({
                            workspaceId: workspace.id,
                            organizationId,
                          })
                            .unwrap()
                            .then(() => {
                              notify.success('Joined workspace');
                              replaceItem(workspace.id, {
                                ...workspace,
                                isMember: true,
                                totalMembers: workspace.totalMembers + 1,
                              });
                            })
                            .catch(() => {
                              notify.error('Failed to join workspace');
                            });
                        }}
                      />
                    )
              }
            />
          </div>
        ),
      },
      {
        alignment: 'center',
        title: 'Shared folders',
        width: 100,
        maxContentWidth: 100,
        render: (workspace) => (
          <div className="flex justify-center items-center">
            <TextBase
              color="text-ds-natural-600"
              label={workspace?.totalSharedFolders}
            />
          </div>
        ),
      },
      {
        alignment: 'left',
        title: 'Tags',
        width: 350,
        maxContentWidth: 350,
        render: (item) =>
          tags?.length > 0 && (
            <WorkspacesPageTagsCell
              tags={tags.filter(
                (tag) => item?.tags?.length && item.tags.includes(tag.id)
              )}
            />
          ),
      },
    ],
    [joinWorkspace, organizationId, replaceItem, tags]
  );

  const isEmpty = !isLoading && workspaces?.length < 1;
  const tableParentRef = useRef<HTMLDivElement>();
  const [listBottomRef, _, entry] = useInView({
    root: tableParentRef.current,
  });

  useEffect(() => {
    if (workspaces?.length && entry?.isIntersecting) {
      handleFetchNextBatch();
    }
  }, [entry, handleFetchNextBatch, workspaces]);

  return (
    <div className="w-full h-full flex overflow-hidden">
      {isEmpty ? (
        <WorkspacesPageEmpty />
      ) : (
        <div
          ref={tableParentRef}
          className="relative overflow-y-auto"
        >
          <FixedTable<Workspace>
            isLoadingBatch={isLoadingBatch}
            headerRowClassName="pt-4 pb-1 sticky top-0 bg-ds-accent3-100 z-[5]"
            cols={cols}
            data={workspaces}
            isLoading={isLoading}
            loader={<WorkspacesPageTableSkeleton />}
            rowWrapper={WorkspacesPageTableRowWrapper}
            noWrapperDiv
          />
          <div ref={listBottomRef} />
        </div>
      )}
    </div>
  );
};
